import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { CategoryAttribute } from 'Douleutaras/redux/data/v2/categoryForm/types';
import { useAppDispatch, useAppSelector } from 'Douleutaras/redux/hooks';
import { selectCurrentUser } from 'Douleutaras/redux/data/auth/authSlice';
import { Spin } from 'Douleutaras/functional-components/Spin/Spin';
import { Gis } from 'Douleutaras/components/Forms/gis.class';
import { sellerProfilesSelectors } from 'Douleutaras/redux/data/seller/sellerProfilesSlice';
import ModalSelectionItem from 'Douleutaras/components/modals/parts/ModalSelectionItem';
import SkeletonLoader from 'Douleutaras/components/Loaders/SkeletonLoader';
import { useSearchParams } from 'react-router-dom';
import { categoryFormSelectors } from 'Douleutaras/redux/data/v2/categoryForm/categoryFormSlice';
import { POST_JOB_FLOW } from 'Douleutaras/views/job/post-job/types';
import { PostHogEventNames } from 'Douleutaras/app/lib/posthog/posthog.provider';
import { useAB } from 'Douleutaras/app/lib/ab';
import { EXPERIMENT_VARIANTS, PD_AVATARS_EXPERIMENT_KEY } from 'Douleutaras/constants/ab/experiments.const';
import { FLOW_TYPE_TO_EVENT_PROP } from 'Douleutaras/constants/job';

import usePostJobHelpers, { PostJobSteps, StepStatus, usePostJobProgress } from './hooks/post-job';
import {
  backButtonEffect,
  fetchDataEffect,
  handleReferrerEffect,
  initializeCategoryFormId,
  nextButtonEffect,
  saveToLocalStorageEffect,
  setCallOutFeeEffect,
  setTitleEffect,
  subscribeToQuestionChangesEffect,
  updateProgressBarEffect
} from './effects/post-job-effects';
import UserStep, { CheckUserResponse } from './steps/UserStep';
import LocationStep from './steps/LocationStep';
import CategoryStep from './steps/CategoryStep';
import QuestionsStep from './steps/QuestionsStep';
import './PostJobForm.scss';

interface PostJobFormProps {
  iframe?: boolean;
  autoDealIds: Array<string>;
  categoryId: string;
  postJobFlow: POST_JOB_FLOW;
  proId: string;
  proUsername: string;
  isFormValid: boolean;
  isFormSubmitting: boolean;
  initialValues: {};
  initialQuestionsHistory: Array<number>;
  jumpTo: PostJobSteps;
  progressBarUpdate?({ progressTranslateX }): void;
  setIsFormValid(enabled: boolean): void;
  setIsFormSubmitting(enabled: boolean): void;
  setNextButtonEnabled(enabled: boolean): void;
  setNextButtonText(label: string | React.ReactElement): void;
  setBackButtonEnabled(enabled: boolean): void;
  stepChangeCallback(questions): void;
  // Reports the location-step inline map-picker state so the modal can swap its
  // action button for a Confirm button. Undefined when the map picker is closed.
  setLocationMapView?(state?: { confirmDisabled: boolean }): void;
}

const PostJobForm = ({
                       iframe,
                       autoDealIds,
                       categoryId,
                       postJobFlow,
                       proId,
                       proUsername,
                       progressBarUpdate,
                       setIsFormValid,
                       setIsFormSubmitting,
                       setNextButtonEnabled,
                       setNextButtonText,
                       setBackButtonEnabled,
                       setLocationMapView,
                       stepChangeCallback = () => true,
                       initialValues,
                       initialQuestionsHistory,
                       isFormValid,
                       isFormSubmitting,
                       jumpTo
}: PostJobFormProps,
                     ref) => {
  const [ currentStep, goToStep ] = useState<PostJobSteps>();
  const [ prevStep, setPrevStep ] = useState<Array<PostJobSteps>>([]);
  const [ userGis, setUserGis ] = useState<Gis>();
  const [ currentTitle, setCurrentTitle ] = useState<string | React.ReactElement>('');
  const [ currentNote, setCurrentNote ] = useState<string | React.ReactElement>('');
  const [ currentFee, setCurrentFee ] = useState<string | React.ReactElement>('');
  // If we need to "skip" steps for some reason, they should be inserted into the history
  const [ questionsStepHistory, setQuestionsStepHistory ] = useState<Array<number>>(initialQuestionsHistory || []);
  const [ callOutFeeStepHistory, setCallOutFeeStepHistory ] = useState<Array<number>>([]);
  const [ showNextButton, setShowNextButton ] = useState<boolean>();
  const [ stepStatus, setStepStatus ] = useState<StepStatus>();
  const [ hasCallOutFeeStep, setHasCallOutFeeStep ] = useState<boolean>();
  const [ validAddresses, setValidAddresses ] = useState<Array<string>>([]); // Location step
  //  inline map-picker view state (hides title/note + swaps the modal action button)
  const [ locationMapActive, setLocationMapActive ] = useState(false);
  const [ locationMapConfirmDisabled, setLocationMapConfirmDisabled ] = useState(true);
  const mapConfirmRef = useRef<(() => void) | undefined>(undefined);
  const mapCloseRef = useRef<(() => void) | undefined>(undefined);
  const [ userCheckResponse, setUserCheckResponse ] = useState<CheckUserResponse>(); // User step
  const postingFlowResponse = useAppSelector(state => state.pros.postingFlow);

  // Owns the personal-details A/B: initialized once here so the flag resolves when the flow
  // opens and is cached by the time the USER_STEP (Personal Details) renders.
  const ab = useAB({ experimentKey: PD_AVATARS_EXPERIMENT_KEY, initializeExperiment: true });
  const isProAvatarsVariant =
    ab.appliedExperimentVariant === EXPERIMENT_VARIANTS[PD_AVATARS_EXPERIMENT_KEY].EXPERIMENT_VARIANT_B;
  const bidObjective = postingFlowResponse?.max_bids;
  const availableProfessionals = postingFlowResponse?.pros_available_info?.pros_available;
  // Variant B replaces the personal-details step header (title + note) with the pro-banner,
  // so the shared title/note are hidden only on that step.
  const hidePersonalDetailsHeader = isProAvatarsVariant && currentStep === PostJobSteps.USER_STEP;

  const [questionsInnerProgress, setQuestionsInnerProgress] = useState(0);
  const { progressTranslateX } = usePostJobProgress(currentStep, questionsInnerProgress);

  const dispatch = useAppDispatch();
  const profile = useAppSelector(state => sellerProfilesSelectors.selectById(state, proId));
  const categoryForm = useAppSelector(state => categoryFormSelectors.selectById(state, categoryId));
  const [ searchParams ] = useSearchParams();
  const user = useAppSelector(selectCurrentUser);
  const isDirectContact = postJobFlow === POST_JOB_FLOW.DIRECT_CONTACT;

  const defaultFormValues = {
    categoryId,
    categoryFormId: undefined,
    questions: {},
    marketing: user?.marketing_consent_granted || false,
    email: user?.email,
    phone: user?.phone,
    name: user?.get_full_name,
    existingUser: Boolean(user?.id) || false, // True if logged out but existing. Is set in UserStep
    proId,
    autoDealIds,
    ...initialValues
  };

  const formMethods = useForm({
    defaultValues: defaultFormValues,
    mode: 'onBlur'
  });

  const {
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { isValid, isSubmitted }
  } = formMethods;
  // Dynamic settings for post job
  const postJobSettings = {
    EXISTING_USER_CONTACT_DETAILS_STEP: [
      POST_JOB_FLOW.SINGLE_BID,
      POST_JOB_FLOW.MULTI_BID,
      POST_JOB_FLOW.LEGACY_FALLBACK,
      POST_JOB_FLOW.FASTLANE_SINGLE,
      POST_JOB_FLOW.FASTLANE_MULTI
    ].includes(postJobFlow),
    REDIRECT_TO_PRO_LIST: [ POST_JOB_FLOW.SINGLE_BID, POST_JOB_FLOW.MULTI_BID ].includes(postJobFlow),

    // Will check if the specific PRO is available at user entered location (when proId exists)
    INDIVIDUAL_GEO_CHECK: postJobFlow === POST_JOB_FLOW.DIRECT_CONTACT,
    CAN_JUMP_TO_STEP: [ POST_JOB_FLOW.SINGLE_BID, POST_JOB_FLOW.MULTI_BID ].includes(postJobFlow)
  };

  const allChoices = watch();
  const {
    determineNextButtonText,
    getCurrentTitle,
    onSubmit,
    goToNextStep
  } = usePostJobHelpers({
    currentStep,
    postJobFlow,
    formMethods,
    goToStep,
    hasCallOutFeeStep,
    proId,
    setIsFormSubmitting,
    setPrevStep,
    userGis,
    postJobSettings,
    autoDealIds,
    isProAvatarsVariant,
    bidObjective
  });

  const questionsRef = useRef({
    showNextButton: false
  });

  useEffect(() => fetchDataEffect(
    proId,
    profile,
    dispatch,
    isValid,
    jumpTo,
    postJobSettings,
    proUsername,
    searchParams,
    setPrevStep,
    setValue,
    user,
    watch(),
    goToStep
  ), []);
  useEffect(() => setTitleEffect(getCurrentTitle, setCurrentTitle, setCurrentNote), [currentStep]);
  useEffect(() => setNextButtonEnabled(showNextButton), [showNextButton, currentStep]);
  useEffect(() => subscribeToQuestionChangesEffect(watch, stepChangeCallback), [ stepChangeCallback ]);

  const stepBack = (): void => {
    goToStep(prevStep.pop());
    setPrevStep(prevStep);

    // Trigger for validation on step back
    trigger();
  };

  // Handle how ENTER key is pressed
  const handleKeyDown = event => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent the form from submitting on Enter

      if (typeof questionsRef?.current?.switchToNextStep === 'function') {
        questionsRef.current.switchToNextStep();
      } else if (isFormValid && !isFormSubmitting) {
        goToNextStep();
      }

    }
  };

  const handleResultChecked = data => {
    if (data.job_latitude && data.job_longitude) {
      // @AB PostHog event
      ab.capture(PostHogEventNames.LOCATION_SELECTED, {
        ...data,
        category_slug: postingFlowResponse.category_slug,
        sub_category_slug: postingFlowResponse.sub_category_slug,
        business_unit_slug: postingFlowResponse.business_unit_slug
      });
    }
  };

  // Receives the location step's inline map-picker state. Keeps the confirmation and close
  // handlers in refs (invoked from the modal action and close buttons) and drives
  // title/note hiding.
  const handleLocationMapViewChange = useCallback((state?: { confirmDisabled: boolean; confirm(): void; close(): void }) => {
    mapConfirmRef.current = state?.confirm;
    mapCloseRef.current = state?.close;
    setLocationMapActive(Boolean(state));
    setLocationMapConfirmDisabled(state ? state.confirmDisabled : true);
  }, []);

  useEffect(() => {
    setLocationMapView?.(locationMapActive ? { confirmDisabled: locationMapConfirmDisabled } : undefined);
  }, [ locationMapActive, locationMapConfirmDisabled ]);

  // OnSubmit function when a user submits CATEGORY_STEP
  // tslint:disable-next-line:no-shadowed-variable
  const submitCategory = useCallback(({ categoryId }: { categoryId: string }) => {
    setValue('categoryId', categoryId);

    if (!currentStep) {
      goToNextStep();
    }
  }, []);

  // OnSubmit function when a user submits QUESTIONS_STEP
  const submitQuestion = useCallback((hasMoreQuestions: boolean) => {

    if (!hasMoreQuestions) {
      // @AB PostHog event
      ab.capture(PostHogEventNames.JOB_FORM_QUESTIONS_ANSWERED, {
        flow_type: [ POST_JOB_FLOW.SINGLE_BID, POST_JOB_FLOW.MULTI_BID ].includes(postJobFlow)
          ? FLOW_TYPE_TO_EVENT_PROP.SINGLE_BID
          : FLOW_TYPE_TO_EVENT_PROP.LEGACY,
        category_slug: postingFlowResponse.category_slug,
        sub_category_slug: postingFlowResponse.sub_category_slug,
        business_unit_slug: postingFlowResponse.business_unit_slug,
        total_questions: Object.keys(allChoices.questions).length
      });
      goToNextStep();
    }
  }, [ allChoices.questions, currentStep, JSON.stringify(categoryForm), JSON.stringify(postingFlowResponse) ]);

  useEffect(() => {
    setNextButtonText(determineNextButtonText(stepStatus));
  }, [stepStatus, currentStep, postJobFlow, isProAvatarsVariant, bidObjective]);

  useImperativeHandle(ref, () => {

    return {
      goToNextStep,
      categoryId: allChoices.categoryId,
      currentTitle,
      currentNote,
      currentStep,
      stepBack,
      questionsRef,
      confirmLocationMap: () => mapConfirmRef.current?.(),
      closeLocationMap: () => mapCloseRef.current?.()
    };
  }, [currentStep, currentTitle, currentNote, prevStep, questionsRef, hasCallOutFeeStep, postJobFlow]);

  const onLoadQuestion = (question: CategoryAttribute) => {
    if (question?.attribute?.title !== undefined) {
      setCurrentTitle(question.attribute.title);
      setCurrentNote(question.attribute.description);
    }
    if (question?.attribute?.call_out_fee !== undefined && question?.attribute?.call_out_fee !== '0.00') {
      setCurrentFee(question.attribute.call_out_fee);
    }
  };

  // Initialize categoryFormId
  useEffect(() => {
    initializeCategoryFormId(setValue, categoryForm, dispatch, allChoices.categoryId);
  }, [ allChoices.categoryId ]);

  // Controls the "next" button
  useEffect(() => nextButtonEffect(setShowNextButton, currentStep, setStepStatus, hasCallOutFeeStep, user, postJobSettings),
    [ currentStep, hasCallOutFeeStep ]
  );

  // Control "Back" button
  useEffect(() => backButtonEffect(questionsStepHistory, currentStep, prevStep, setBackButtonEnabled),
    [currentStep, JSON.stringify(questionsStepHistory)]);

  useEffect(() => {
    setIsFormValid(isValid);
  }, [ isValid ]);

  // Form validation
  useEffect(() => saveToLocalStorageEffect(
    setIsFormValid,
    isSubmitted,
    isValid,
    allChoices,
    isDirectContact,
    watch,
    questionsStepHistory,
    currentStep
  ), [ allChoices ]);

  useEffect(() => updateProgressBarEffect(progressBarUpdate, progressTranslateX),
    [ JSON.stringify(allChoices?.questions), progressTranslateX ]);

  // Effect to get the referrer id
  useEffect(() => handleReferrerEffect(setValue, searchParams), []);

  // Call out fee determine
  useEffect(() => setCallOutFeeEffect(categoryForm, setHasCallOutFeeStep, watch), [ JSON.stringify(categoryForm) ]);

  if (!profile && !allChoices.categoryId) {
    return <div>
      <div styleName="title">
        <SkeletonLoader type="line" height={25}/>
      </div>
      <div styleName="note">
      </div>
      <div>
        <ModalSelectionItem>
          <SkeletonLoader type="line" height={40}/>
        </ModalSelectionItem>
        <ModalSelectionItem>
          <SkeletonLoader type="line" height={40}/>
        </ModalSelectionItem>
        <ModalSelectionItem>
          <SkeletonLoader type="line" height={40}/>
        </ModalSelectionItem>
      </div>
    </div>;
  }

  return <FormProvider {...formMethods}>
    {!locationMapActive && !hidePersonalDetailsHeader &&
      <div styleName="title">
        {currentTitle}
        {currentFee && currentStep === PostJobSteps.DIAGNOSIS_STEP &&
          <span styleName="fee">
            {parseFloat(currentFee as string)}
          </span>
        }
      </div>
    }
    {!locationMapActive && !hidePersonalDetailsHeader && currentNote &&
      <div styleName="note">
        {currentNote}
      </div>
    }
    <Spin>
      <form onSubmit={handleSubmit(onSubmit)} ref={ref} onKeyDown={handleKeyDown}>
      { currentStep === PostJobSteps.CATEGORY_STEP &&
        <div>
          <CategoryStep onSubmit={submitCategory} />
        </div>
      }
      { currentStep === PostJobSteps.QUESTIONS_STEP &&
        <div>
          <QuestionsStep
            onLoadQuestion={onLoadQuestion}
            onSubmit={submitQuestion}
            categoryId={allChoices.categoryId}
            ref={questionsRef}
            showOnlyCallOutFeeQuestions={false}
            questionsStepHistory={questionsStepHistory}
            setQuestionsStepHistory={setQuestionsStepHistory}
            setShowNextButton={setShowNextButton}
            setHasCallOutFeeStep={setHasCallOutFeeStep}
            setStepStatus={setStepStatus}
            onInnerProgressChange={setQuestionsInnerProgress}
          />
        </div>
      }
      { currentStep === PostJobSteps.LOCATION_STEP &&
        <div>
          <LocationStep
            validAddresses={validAddresses}
            setValidAddresses={setValidAddresses}
            categoryId={allChoices.categoryId}
            postJobSettings={postJobSettings}
            setUserGis={setUserGis}
            proId={proId}
            onResultChecked={handleResultChecked}
            onMapViewChange={handleLocationMapViewChange}
          />
        </div>
      }
      { currentStep === PostJobSteps.USER_STEP &&
        <div>
          <UserStep
            autoDealIds={autoDealIds}
            categoryId={allChoices.categoryId}
            postJobSettings={postJobSettings}
            userCheckResponse={userCheckResponse}
            showProsBanner={isProAvatarsVariant}
            bidObjective={bidObjective}
            availableProfessionals={availableProfessionals}
            setUserCheckResponse={setUserCheckResponse}
          />
        </div>
      }
      {
        currentStep === PostJobSteps.DIAGNOSIS_STEP &&
        <div>
          <QuestionsStep
            onLoadQuestion={onLoadQuestion}
            onSubmit={submitQuestion}
            categoryId={allChoices.categoryId}
            ref={questionsRef}
            questionsStepHistory={callOutFeeStepHistory}
            setQuestionsStepHistory={setCallOutFeeStepHistory}
            setShowNextButton={setShowNextButton}
            showOnlyCallOutFeeQuestions
            setStepStatus={setStepStatus}
          />
        </div>
      }
      </form>
    </Spin>
  </FormProvider>;
};

export default forwardRef(PostJobForm);
